import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sidebar } from '../components/Sidebar';
import { rentalSandboxAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  Plus, Play, Copy, Trash2, Star, ArrowRight, Loader2,
  Sparkles, ChevronDown, ChevronUp, Download, BarChart3,
  Save, Share2, BookMarked,
} from 'lucide-react';
import SalePaybackPanel from '../components/SalePaybackPanel';
import {
  formatINR, formatKWh, formatPricePerKwhMonth, formatPercent, formatHours,
} from '../utils/format';

const DEFAULT_BASE_INPUTS = {
  e_kwh: 482,
  pcs_kw: 250,
  capex: 5500000,
  gst: 0.18,
};

const DEFAULT_LEVERS = {
  irr: 0.18,
  tenor_years: 5,
  deposit_months: 3,
  min_commit_pct: 0.70,
  eta: 0.95,
  delivered_kwh: 0,
};

// Local INR/percent helpers — match the shared utility for backwards-compat.
// `formatINR(v, {decimals: 2})` gives the same output as the old FMT_INR.
const FMT_INR = (v) => v == null ? '—' : formatINR(v, { decimals: 2 });
const FMT_PCT = (v) => v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`;

function InputField({ label, field, value, onChange, type = 'number', step, prefix, suffix }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-500 font-medium">{label}</Label>
      <div className="relative mt-0.5">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{prefix}</span>}
        <Input type={type} step={step} value={value}
          onChange={e => onChange(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          className={`h-8 text-xs font-mono ${prefix ? 'pl-6' : ''} ${suffix ? 'pr-10' : ''}`} data-testid={`sandbox-${field}`} />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{suffix}</span>}
      </div>
    </div>
  );
}

function CRFOutputPanel({ outputs }) {
  if (!outputs) return null;
  const sections = [
    { title: 'Service Fee', rows: [
      ['Service Fee (ex-GST)', formatPricePerKwhMonth(outputs.service_fee_ex_gst)],
      ['Service Fee (incl GST)', formatPricePerKwhMonth(outputs.service_fee_incl_gst)],
      ['CRF', outputs.crf?.toFixed(6)],
      ['Annual Recovery', formatINR(outputs.annual_recovery)],
    ]},
    { title: 'Units & Billing', rows: [
      ['Annual Units', formatKWh(outputs.annual_units)],
      ['Base Units Monthly', `${formatKWh(outputs.base_units_monthly, { unit: false })} kWh/mo`],
      ['Min Billable Monthly (70%)', `${formatKWh(outputs.min_billable_monthly, { unit: false })} kWh/mo`],
      ['Billed Units', formatKWh(outputs.billed_units)],
    ]},
    { title: 'Invoice & Deposit', rows: [
      ['Invoice (ex-GST)', formatINR(outputs.invoice_ex_gst)],
      ['Invoice (incl GST)', formatINR(outputs.invoice_incl_gst)],
      ['Min Monthly Invoice (incl GST)', formatINR(outputs.min_monthly_invoice_incl_gst)],
      ['Deposit', formatINR(outputs.deposit)],
    ]},
    { title: 'Internal Metrics', rows: [
      ['Hours @ Rated Power', formatHours(outputs.hours_at_rated_power)],
    ]},
  ];

  return (
    <div className="space-y-4">
      {sections.map((sec, si) => (
        <div key={si}>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{sec.title}</p>
          <div className="grid grid-cols-1 gap-0">
            {sec.rows.map(([label, val], i) => (
              <div key={label || i} className="flex justify-between text-xs py-1.5 border-b border-slate-100 last:border-0">
                <span className="text-slate-500">{label}</span>
                <span className="font-mono font-medium text-slate-900">{val}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {outputs.data_quality_flags?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-700">
          {outputs.data_quality_flags.map((f, i) => <p key={f || i}>Warning: {f}</p>)}
        </div>
      )}
    </div>
  );
}

function CompareTable({ comparison }) {
  if (!comparison?.length) return null;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded">
      <table className="w-full text-xs" data-testid="compare-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-2 text-left">Scenario</th>
            <th className="px-3 py-2 text-right">Tenor</th>
            <th className="px-3 py-2 text-right">IRR</th>
            <th className="px-3 py-2 text-right">Fee ex-GST</th>
            <th className="px-3 py-2 text-right">Fee incl-GST</th>
            <th className="px-3 py-2 text-right">Min Monthly</th>
            <th className="px-3 py-2 text-right">Deposit</th>
            <th className="px-3 py-2 text-right">Base Units</th>
          </tr>
        </thead>
        <tbody>
          {comparison.map((row, i) => (
            <tr key={row.name || i} className={`border-t border-slate-100 ${i === 0 ? 'bg-blue-50/30 font-medium' : 'hover:bg-slate-50'}`}>
              <td className="px-3 py-2 font-medium">{row.name}</td>
              <td className="px-3 py-2 text-right font-mono">{row.tenor_years}yr</td>
              <td className="px-3 py-2 text-right font-mono">{FMT_PCT(row.irr)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatPricePerKwhMonth(row.service_fee_ex_gst)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatPricePerKwhMonth(row.service_fee_incl_gst)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatINR(row.min_monthly_invoice_incl_gst)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatINR(row.deposit)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatKWh(row.base_units_monthly, { unit: false })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AIPanel({ runId, onScenariosCreated }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);

  const handleSimulate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await rentalSandboxAPI.aiSimulate({ prompt, run_id: runId });
      setResult(res.data);
    } catch (e) { toast.error(e.userMessage || 'AI simulation failed'); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!result?.scenarios) return;
    setApplying(true);
    try {
      await rentalSandboxAPI.aiApply({
        run_id: runId,
        scenarios: result.scenarios.map(s => ({
          scenario_name: s.scenario_name,
          lever_overrides: s.lever_overrides,
        })),
      });
      toast.success(`${result.scenarios.length} scenarios created`);
      setResult(null);
      setPrompt('');
      onScenariosCreated?.();
    } catch (e) { toast.error(e.userMessage || 'Failed to apply scenarios'); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded p-4 space-y-3" data-testid="ai-panel">
      <div className="flex items-center space-x-2">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        <p className="text-xs font-bold text-indigo-700">AI Simulation (CRF/IRR)</p>
      </div>
      <div className="flex space-x-2">
        <Input value={prompt} onChange={e => setPrompt(e.target.value)} placeholder='e.g. "Compare 3yr vs 5yr vs 7yr at 18% IRR"'
          className="text-xs h-9 flex-1 bg-white" data-testid="ai-prompt-input"
          onKeyDown={e => e.key === 'Enter' && handleSimulate()} />
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs h-9" onClick={handleSimulate} disabled={loading} data-testid="ai-simulate-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
          Simulate
        </Button>
      </div>

      {result && result.success && (
        <div className="space-y-2 bg-white rounded border border-indigo-100 p-3">
          {result.summary && <p className="text-xs text-slate-600">{result.summary}</p>}
          {result.interpreted_inputs?.description && (
            <div className="text-[10px] bg-indigo-50 rounded p-2">
              <span className="font-semibold text-indigo-700">Interpreted: </span>
              <span className="text-indigo-600">{result.interpreted_inputs.description}</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]" data-testid="ai-results-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-1 text-left">Scenario</th>
                  <th className="px-2 py-1 text-right">Fee/kWh</th>
                  <th className="px-2 py-1 text-right">Min Monthly</th>
                  <th className="px-2 py-1 text-right">Deposit</th>
                </tr>
              </thead>
              <tbody>
                {result.scenarios.map((s, i) => (
                  <tr key={s.scenario_name || i} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-medium">{s.scenario_name}</td>
                    <td className="px-2 py-1 text-right font-mono">{FMT_INR(s.outputs?.service_fee_ex_gst)}</td>
                    <td className="px-2 py-1 text-right font-mono">{FMT_INR(s.outputs?.min_monthly_invoice_incl_gst)}</td>
                    <td className="px-2 py-1 text-right font-mono">{FMT_INR(s.outputs?.deposit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs w-full" onClick={handleApply} disabled={applying} data-testid="ai-apply-btn">
            {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArrowRight className="w-3 h-3 mr-1" />}
            Apply & Create {result.scenarios.length} Scenarios
          </Button>
        </div>
      )}

      {result && !result.success && (
        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-600">{result.error || 'AI parsing failed'}</div>
      )}
    </div>
  );
}

export default function RentalSandboxPage() {
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [baseInputs, setBaseInputs] = useState({ ...DEFAULT_BASE_INPUTS });
  const [levers, setLevers] = useState({ ...DEFAULT_LEVERS });
  const [comparison, setComparison] = useState(null);
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioOverrides, setScenarioOverrides] = useState({});
  const [presets, setPresets] = useState([]);
  const [view, setView] = useState('outputs');
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetForm, setPresetForm] = useState({ name: '', description: '', is_shared: false });
  const [presetsExpanded, setPresetsExpanded] = useState(false);
  // Controls visibility of CRF (Rental) output vs. Sale Model Payback panel.
  // Persisted on the run so reopening restores the same view.
  const [proposalType, setProposalType] = useState('rental'); // "sale" | "rental" | "both"

  const fetchRuns = useCallback(async () => {
    try {
      const res = await rentalSandboxAPI.listRuns();
      setRuns(res.data);
    } catch (e) { toast.error(e.userMessage || 'Failed to load runs'); }
    finally { setLoading(false); }
  }, []);

  const fetchRun = useCallback(async (runId) => {
    try {
      const res = await rentalSandboxAPI.getRun(runId);
      setSelectedRun(res.data);
      // Normalize legacy `capex` ↔ canonical `capex_ex_gst` so the form input
      // always renders even on runs saved before the audit-pass migration.
      const bi = res.data.base_inputs_json || { ...DEFAULT_BASE_INPUTS };
      if (bi.capex == null && bi.capex_ex_gst != null) bi.capex = bi.capex_ex_gst;
      setBaseInputs(bi);
      setLevers(res.data.lever_inputs_json || { ...DEFAULT_LEVERS });
      setProposalType(res.data.proposal_type || 'rental');
    } catch (e) { toast.error(e.userMessage || 'Failed to load run'); }
  }, []);

  const fetchComparison = useCallback(async (runId) => {
    try {
      const res = await rentalSandboxAPI.compare(runId);
      setComparison(res.data.comparison);
    } catch (e) { console.error(e); }
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await rentalSandboxAPI.getPresets();
      setPresets(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchRuns(); fetchPresets(); }, [fetchRuns, fetchPresets]);

  const handleCreateRun = async () => {
    setCreating(true);
    try {
      const res = await rentalSandboxAPI.createRun({
        title: `Run ${new Date().toLocaleDateString()}`,
        base_inputs: baseInputs,
        lever_inputs: levers,
        proposal_type: proposalType,
      });
      toast.success('Run created');
      await fetchRuns();
      setSelectedRun(res.data);
    } catch (e) { toast.error(e.userMessage || 'Failed to create scenario run'); }
    finally { setCreating(false); }
  };

  const handleUpdateRun = async () => {
    if (!selectedRun) return;
    try {
      const res = await rentalSandboxAPI.updateRun(selectedRun.run_id, {
        base_inputs: baseInputs,
        lever_inputs: levers,
      });
      setSelectedRun(res.data);
      toast.success('Recalculated');
      fetchRuns();
      if (view === 'compare') fetchComparison(selectedRun.run_id);
    } catch (e) { toast.error(e.userMessage || 'Failed to update run'); }
  };

  const handleAddScenario = async () => {
    if (!selectedRun) return;
    try {
      await rentalSandboxAPI.createScenario(selectedRun.run_id, {
        scenario_name: scenarioName || 'Scenario',
        lever_overrides: scenarioOverrides,
      });
      toast.success('Scenario added');
      fetchRun(selectedRun.run_id);
      fetchComparison(selectedRun.run_id);
      setShowScenarioForm(false);
      setScenarioName('');
      setScenarioOverrides({});
    } catch (e) { toast.error(e.userMessage || 'Failed to add scenario'); }
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!selectedRun) return;
    await rentalSandboxAPI.deleteScenario(selectedRun.run_id, scenarioId);
    fetchRun(selectedRun.run_id);
    fetchComparison(selectedRun.run_id);
  };

  const handleDuplicate = async () => {
    if (!selectedRun) return;
    const res = await rentalSandboxAPI.duplicateRun(selectedRun.run_id);
    toast.success('Duplicated');
    fetchRuns();
    setSelectedRun(res.data);
  };

  const handleDelete = async (runId) => {
    await rentalSandboxAPI.deleteRun(runId);
    toast.success('Deleted');
    if (selectedRun?.run_id === runId) setSelectedRun(null);
    fetchRuns();
  };

  const handlePreset = (preset) => {
    if (preset.levers) setLevers(prev => ({ ...prev, ...preset.levers }));
    if (preset.base_inputs) setBaseInputs(prev => ({ ...prev, ...preset.base_inputs }));
    toast.success(`Applied: ${preset.name}`);
  };

  const handleSavePreset = async () => {
    if (!presetForm.name.trim()) { toast.error('Name required'); return; }
    try {
      if (selectedRun) {
        await rentalSandboxAPI.savePresetFromRun(selectedRun.run_id, {
          name: presetForm.name,
          description: presetForm.description,
          is_shared: presetForm.is_shared,
        });
      } else {
        await rentalSandboxAPI.createPreset({
          name: presetForm.name,
          description: presetForm.description,
          levers,
          base_inputs: baseInputs,
          is_shared: presetForm.is_shared,
        });
      }
      toast.success('Preset saved');
      setShowSavePreset(false);
      setPresetForm({ name: '', description: '', is_shared: false });
      fetchPresets();
    } catch (e) { toast.error(e.userMessage || 'Failed to save preset'); }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await rentalSandboxAPI.deletePreset(presetId);
      toast.success('Preset deleted');
      fetchPresets();
    } catch (e) { toast.error(e.userMessage || e.response?.data?.detail || 'Failed to delete preset'); }
  };

  const handleToggleShared = async (preset) => {
    try {
      await rentalSandboxAPI.updatePreset(preset.preset_id, { is_shared: !preset.is_shared });
      toast.success(preset.is_shared ? 'Preset made private' : 'Preset shared with team');
      fetchPresets();
    } catch (e) { toast.error(e.userMessage || e.response?.data?.detail || 'Failed to update preset'); }
  };

  const handleExportCSV = () => {
    if (!comparison) return;
    const headers = ['Scenario', 'Tenor (yr)', 'IRR', 'Fee ex-GST', 'Fee incl-GST', 'Min Monthly Invoice', 'Deposit', 'Base Units/mo'];
    const rows = comparison.map(r => [r.name, r.tenor_years, r.irr, r.service_fee_ex_gst, r.service_fee_incl_gst, r.min_monthly_invoice_incl_gst, r.deposit, r.base_units_monthly]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rental_sandbox_crf_${selectedRun?.run_id || 'export'}.csv`;
    a.click();
  };

  const setBase = (k, v) => setBaseInputs(prev => ({ ...prev, [k]: v }));
  const setLever = (k, v) => setLevers(prev => ({ ...prev, [k]: v }));
  const setOverride = (k, v) => setScenarioOverrides(prev => ({ ...prev, [k]: v }));

  return (
    <div className="flex h-screen bg-slate-50" data-testid="rental-sandbox-page">
      <Sidebar />
      <div className="flex-1 ml-64 flex overflow-hidden">
        {/* Run List */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-700">Sandbox Runs</p>
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={handleCreateRun} disabled={creating} data-testid="create-run-btn">
              <Plus className="w-3 h-3 mr-0.5" /> New
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <p className="text-xs text-slate-400 p-3">Loading...</p> : runs.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">No runs yet. Click "New" to start.</div>
            ) : runs.map(run => (
              <button key={run.run_id}
                onClick={() => { fetchRun(run.run_id); fetchComparison(run.run_id); }}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${selectedRun?.run_id === run.run_id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''}`}
                data-testid={`run-${run.run_id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-700 truncate">{run.title}</p>
                  {run.is_starred && <Star className="w-3 h-3 text-amber-400 fill-amber-400" />}
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">{run.scenario_count || 0} scenarios</p>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        {!selectedRun ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-500">Rental Sandbox (CRF/IRR)</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">Create a new run to explore rental pricing using the standardized CRF/IRR method with 1-cycle/day baseline.</p>
              <Button size="sm" className="mt-4 bg-indigo-600 text-xs" onClick={handleCreateRun} disabled={creating} data-testid="create-first-run-btn">
                <Plus className="w-3 h-3 mr-1" /> Create First Run
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Inputs */}
            <div className="w-[340px] bg-white border-r border-slate-200 overflow-y-auto p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Input value={selectedRun.title} className="text-sm font-bold h-8 border-0 px-0 focus:ring-0 shadow-none" readOnly data-testid="run-title" />
                <div className="flex space-x-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleDuplicate} title="Duplicate"><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(selectedRun.run_id)} title="Delete"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>

              {/* Presets Library */}
              <div className="border border-slate-100 rounded p-2.5 space-y-2">
                <div className="flex items-center justify-between">
                  <button onClick={() => setPresetsExpanded(!presetsExpanded)} className="flex items-center space-x-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:text-indigo-600">
                    <BookMarked className="w-3 h-3" />
                    <span>Presets</span>
                    {presetsExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-indigo-600" onClick={() => setShowSavePreset(true)} data-testid="save-preset-btn">
                    <Save className="w-3 h-3 mr-0.5" /> Save
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(presets.system || []).map((p) => (
                    <button key={p.preset_id} onClick={() => handlePreset(p)}
                      className="text-[10px] bg-slate-50 hover:bg-indigo-50 text-slate-600 hover:text-indigo-700 px-2 py-1 rounded border border-slate-200 transition-colors" data-testid={`preset-${p.preset_id}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
                {presetsExpanded && (
                  <div className="space-y-1 pt-1 border-t border-slate-100">
                    <p className="text-[9px] text-slate-400 uppercase font-semibold">My Presets & Shared</p>
                    {(presets.user || []).length === 0 ? (
                      <p className="text-[10px] text-slate-400 italic">No saved presets yet</p>
                    ) : (presets.user || []).map(p => (
                      <div key={p.preset_id} className="flex items-center justify-between bg-white border border-slate-100 rounded px-2 py-1.5 group hover:border-indigo-200" data-testid={`user-preset-${p.preset_id}`}>
                        <button onClick={() => handlePreset(p)} className="flex-1 text-left">
                          <span className="text-[10px] font-medium text-slate-700">{p.name}</span>
                          {p.is_shared && <Share2 className="w-2.5 h-2.5 text-indigo-400 inline ml-1" />}
                        </button>
                        <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleToggleShared(p)} className="p-0.5 hover:text-indigo-500 text-slate-400"><Share2 className="w-2.5 h-2.5" /></button>
                          <button onClick={() => handleDeletePreset(p.preset_id)} className="p-0.5 hover:text-red-500 text-slate-400"><Trash2 className="w-2.5 h-2.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showSavePreset && (
                  <div className="border border-indigo-200 rounded p-2 space-y-1.5 bg-indigo-50/50">
                    <p className="text-[10px] font-bold text-indigo-700">Save as Preset</p>
                    <Input value={presetForm.name} onChange={e => setPresetForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Client X Terms" className="h-7 text-[10px]" data-testid="preset-name-input" />
                    <Input value={presetForm.description} onChange={e => setPresetForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description" className="h-7 text-[10px]" data-testid="preset-desc-input" />
                    <label className="flex items-center space-x-1.5 text-[10px] cursor-pointer">
                      <input type="checkbox" checked={presetForm.is_shared} onChange={e => setPresetForm(prev => ({ ...prev, is_shared: e.target.checked }))}
                        className="rounded" data-testid="preset-shared-check" />
                      <span className="text-slate-600">Share with team</span>
                    </label>
                    <div className="flex space-x-1.5">
                      <Button size="sm" className="bg-indigo-600 text-[10px] h-7 flex-1" onClick={handleSavePreset} data-testid="confirm-save-preset"><Save className="w-3 h-3 mr-0.5" /> Save</Button>
                      <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => setShowSavePreset(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Asset Inputs */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Asset / System</p>
                <InputField label="Battery Energy (E_kWh)" field="e_kwh" value={baseInputs.e_kwh} onChange={setBase} suffix="kWh" />
                <InputField label="PCS Rated Power (PCS_kW)" field="pcs_kw" value={baseInputs.pcs_kw} onChange={setBase} suffix="kW" />
                <InputField label="Capex (ex-GST)" field="capex" value={baseInputs.capex} onChange={setBase} prefix="₹" />
                <InputField label="GST Rate" field="gst" value={baseInputs.gst} onChange={setBase} step="0.01" />
              </div>

              {/* Levers */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Financial Levers</p>
                <InputField label="Target IRR (annual)" field="irr" value={levers.irr} onChange={setLever} step="0.01" />
                <InputField label="Tenor (years)" field="tenor_years" value={levers.tenor_years} onChange={setLever} suffix="yr" />
                <InputField label="Deposit Months" field="deposit_months" value={levers.deposit_months} onChange={setLever} />
                <InputField label="Min Commit %" field="min_commit_pct" value={levers.min_commit_pct} onChange={setLever} step="0.01" />
                <InputField label="Efficiency (eta)" field="eta" value={levers.eta} onChange={setLever} step="0.01" />
                <InputField label="Delivered kWh/month (optional)" field="delivered_kwh" value={levers.delivered_kwh} onChange={setLever} suffix="kWh" />
              </div>

              <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={handleUpdateRun} data-testid="recalculate-btn">
                <Play className="w-3 h-3 mr-1" /> Recalculate (CRF)
              </Button>
            </div>

            {/* Right: Outputs + Compare + AI */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="flex items-center space-x-1 border-b border-slate-200 pb-2">
                {['outputs', 'compare'].map(v => (
                  <button key={v} onClick={() => { setView(v); if (v === 'compare') fetchComparison(selectedRun.run_id); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${view === v ? 'bg-white border border-b-0 border-slate-200 text-indigo-700' : 'text-slate-500 hover:text-slate-700'}`}
                    data-testid={`tab-${v}`}>
                    {v === 'outputs' ? 'Outputs' : 'Compare Scenarios'}
                  </button>
                ))}

                {/* Proposal Type toggle — controls panel visibility per spec */}
                {view === 'outputs' && (
                  <div className="ml-auto flex items-center gap-1" data-testid="proposal-type-toggle">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wide mr-1">Proposal</span>
                    {[
                      { v: 'rental', label: 'Rental' },
                      { v: 'sale', label: 'Sale' },
                      { v: 'both', label: 'Sale or Rental' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        data-testid={`proposal-type-${opt.v}`}
                        onClick={async () => {
                          setProposalType(opt.v);
                          try {
                            await rentalSandboxAPI.updateRun(selectedRun.run_id, { proposal_type: opt.v });
                          } catch (e) { console.error('updateRun proposal_type failed', e); }
                        }}
                        className={`px-2.5 py-1 text-[11px] rounded border transition-colors ${
                          proposalType === opt.v
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                {view === 'compare' && comparison && (
                  <Button size="sm" variant="ghost" className="text-[10px] h-7 ml-auto" onClick={handleExportCSV} data-testid="export-csv-btn">
                    <Download className="w-3 h-3 mr-0.5" /> Export CSV
                  </Button>
                )}
              </div>

              {view === 'outputs' && (
                <>
                  {(proposalType === 'rental' || proposalType === 'both') && (
                    <div className="bg-white border border-slate-200 rounded p-4" data-testid="crf-output-wrapper">
                      <p className="text-xs font-bold text-slate-700 mb-3">CRF/IRR Calculation Results (Rental)</p>
                      <CRFOutputPanel outputs={selectedRun.outputs_json} />
                    </div>
                  )}
                  {(proposalType === 'sale' || proposalType === 'both') && (
                    <div className={proposalType === 'both' ? 'mt-4' : ''}>
                      <SalePaybackPanel
                        system={{
                          energy_kwh: selectedRun.base_inputs_json?.e_kwh ?? baseInputs.e_kwh,
                          pcs_kw: selectedRun.base_inputs_json?.pcs_kw ?? baseInputs.pcs_kw,
                        }}
                        capex={selectedRun.base_inputs_json?.capex ?? baseInputs.capex}
                        defaultOpen={proposalType === 'sale'}
                      />
                    </div>
                  )}
                </>
              )}

              {view === 'compare' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-700">Scenario Comparison</p>
                    <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => setShowScenarioForm(true)} data-testid="add-scenario-btn">
                      <Plus className="w-3 h-3 mr-0.5" /> Add Scenario
                    </Button>
                  </div>
                  {comparison && <CompareTable comparison={comparison} />}

                  {selectedRun.scenarios?.map(s => (
                    <div key={s.scenario_id} className="bg-white border border-slate-100 rounded p-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">{s.scenario_name}</p>
                        <p className="text-[10px] text-slate-400">Overrides: {Object.entries(s.lever_overrides_json || {}).map(([k, v]) => `${k}=${v}`).join(', ') || 'none'}</p>
                      </div>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDeleteScenario(s.scenario_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}

                  {showScenarioForm && (
                    <div className="bg-white border border-indigo-200 rounded p-3 space-y-2">
                      <p className="text-xs font-bold text-indigo-700">New Scenario (lever overrides)</p>
                      <Input value={scenarioName} onChange={e => setScenarioName(e.target.value)} placeholder="Scenario name" className="text-xs h-8" data-testid="scenario-name-input" />
                      <div className="grid grid-cols-2 gap-2">
                        <InputField label="IRR" field="irr" value={scenarioOverrides.irr || ''} onChange={(k, v) => setOverride(k, v)} step="0.01" />
                        <InputField label="Tenor (years)" field="tenor_years" value={scenarioOverrides.tenor_years || ''} onChange={(k, v) => setOverride(k, v)} />
                        <InputField label="Deposit months" field="deposit_months" value={scenarioOverrides.deposit_months || ''} onChange={(k, v) => setOverride(k, v)} />
                        <InputField label="Min Commit %" field="min_commit_pct" value={scenarioOverrides.min_commit_pct || ''} onChange={(k, v) => setOverride(k, v)} step="0.01" />
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" className="bg-indigo-600 text-xs" onClick={handleAddScenario} data-testid="save-scenario-btn">Save</Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowScenarioForm(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Panel */}
              <AIPanel runId={selectedRun.run_id} onScenariosCreated={() => { fetchRun(selectedRun.run_id); fetchComparison(selectedRun.run_id); setView('compare'); }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
